import { normalizeNumberInput } from "@/lib/parse-number";
import { BIG_ZERO, pow10Big } from "@/lib/tool-kit/math/bigint";

/**
 * Typed decimals kept as digit strings, so nothing is lost to a double.
 *
 * The school-arithmetic tools (long division, long multiplication, rounding, expanded
 * form) all need the digits exactly as the reader typed them — `2.50` has two decimal
 * places, `1.005` is not `1.00499999…` — which a `Number` cannot promise. A `Decimal`
 * is a sign, an integer part without leading zeros and a fractional part with its
 * trailing zeros intact.
 */
export interface Decimal {
  negative: boolean;
  /** Digits before the point, no leading zeros ("0" for 0.5). */
  int: string;
  /** Digits after the point, trailing zeros kept ("" for a whole number). */
  frac: string;
}

/** Most digits (integer + fractional) a tool will accept. */
export const MAX_DECIMAL_DIGITS = 120;

const E_NOTATION = /^([^eE]+)[eE]([+-]?\d+)$/;
const LEADING_ZEROS = /^0+(?=\d)/;
const SIGN = /^[+-]/;
const ZEROS = /0/g;
const MAX_E_EXPONENT = 60;

/** Place the digits of `digits` with `scale` of them after the point. */
function splitDigits(
  digits: string,
  scale: number
): { int: string; frac: string } {
  const padded = digits.padStart(scale + 1, "0");
  const cut = padded.length - scale;
  return {
    int: padded.slice(0, cut).replace(LEADING_ZEROS, ""),
    frac: padded.slice(cut),
  };
}

function shiftByExponent(
  mantissa: string,
  exponent: number
): { int: string; frac: string } | null {
  if (Math.abs(exponent) > MAX_E_EXPONENT) {
    return null;
  }
  const point = mantissa.indexOf(".");
  const digits = mantissa.replace(".", "");
  const scale = point === -1 ? 0 : mantissa.length - point - 1;
  const newScale = scale - exponent;
  if (newScale < 0) {
    return splitDigits(digits + "0".repeat(-newScale), 0);
  }
  return splitDigits(digits, newScale);
}

/**
 * Read a typed number exactly. Accepts a decimal point or a decimal comma, grouping
 * spaces, a plus or minus sign (ASCII or −), and e-notation (`1.5e3`). Null when the
 * text is not a number or has more than {@link MAX_DECIMAL_DIGITS} digits.
 */
export function parseDecimal(raw: string): Decimal | null {
  const text = raw.trim();
  const match = E_NOTATION.exec(text);
  const mantissaText = match ? match[1] : text;
  const normalized = normalizeNumberInput(mantissaText);
  if (normalized === null) {
    return null;
  }
  const negative = normalized.startsWith("-");
  const unsigned = normalized.replace(SIGN, "");
  const exponent = match ? Number.parseInt(match[2], 10) : 0;
  const parts = shiftByExponent(unsigned, exponent);
  if (parts === null) {
    return null;
  }
  const int = parts.int === "" ? "0" : parts.int;
  if (int.length + parts.frac.length > MAX_DECIMAL_DIGITS) {
    return null;
  }
  const isZero = BigInt(int + parts.frac) === BIG_ZERO;
  return { negative: negative && !isZero, int, frac: parts.frac };
}

/** The decimal as an unsigned whole number and the count of digits after the point. */
export function toScaled(d: Decimal): { value: bigint; scale: number } {
  return { value: BigInt(d.int + d.frac), scale: d.frac.length };
}

/** The signed numerator over 10^scale. */
export function toSignedScaled(d: Decimal): { value: bigint; scale: number } {
  const { value, scale } = toScaled(d);
  return { value: d.negative ? -value : value, scale };
}

/** Build a decimal from a signed whole number and a count of fractional digits. */
export function fromScaled(value: bigint, scale: number): Decimal {
  const negative = value < BIG_ZERO;
  const digits = (negative ? -value : value).toString();
  const parts = splitDigits(digits, scale);
  return {
    negative,
    int: parts.int === "" ? "0" : parts.int,
    frac: parts.frac,
  };
}

/** Is the decimal exactly zero? */
export function isZeroDecimal(d: Decimal): boolean {
  return BigInt(d.int + d.frac) === BIG_ZERO;
}

/** True when there is a nonzero digit after the point. */
export function hasFraction(d: Decimal): boolean {
  return d.frac.replace(ZEROS, "") !== "";
}

/** Remove trailing fractional zeros (2.50 → 2.5, 4.00 → 4). */
export function trimDecimal(d: Decimal): Decimal {
  let frac = d.frac;
  while (frac.endsWith("0")) {
    frac = frac.slice(0, -1);
  }
  return { ...d, frac };
}

/**
 * The decimal as text with a "." mark, and a real minus sign (−) when `unicodeMinus`.
 * Machine form: callers format for the reader's locale themselves where it matters.
 */
export function decimalToString(d: Decimal, unicodeMinus = true): string {
  let sign = "";
  if (d.negative) {
    sign = unicodeMinus ? "−" : "-";
  }
  return `${sign}${d.int}${d.frac === "" ? "" : `.${d.frac}`}`;
}

/** Compare |a| with |b|: negative, zero or positive. */
export function compareMagnitude(a: Decimal, b: Decimal): number {
  const scale = Math.max(a.frac.length, b.frac.length);
  const left = BigInt(a.int + a.frac.padEnd(scale, "0"));
  const right = BigInt(b.int + b.frac.padEnd(scale, "0"));
  if (left === right) {
    return 0;
  }
  return left < right ? -1 : 1;
}

/** 10^k as a BigInt (re-exported so tools in this folder need one import). */
export const pow10 = pow10Big;

/** Insert thin grouping into a digit string: "1234567" → "1,234,567" (English). */
export function groupDigits(digits: string, separator = ","): string {
  const out: string[] = [];
  for (let end = digits.length; end > 0; end -= 3) {
    out.unshift(digits.slice(Math.max(0, end - 3), end));
  }
  return out.join(separator);
}
